// @ts-nocheck

import Chevron from '@lmc/ui-kit/src/systemIcons/Chevron/Chevron'
import { NAMESPACE } from 'locale'
import { reportsConstants } from 'locale/team'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getNominative } from '../../../common/utils'
import { DocumentItem } from '../DocumentItem'
import { SectionHeader } from '../SectionHeader'

const groupBySubCategory = (list) => {
  return _.chain(list)
    .orderBy('name')
    .groupBy('subCategory')
    .map((documents, subCategory) => ({
      subCategory:
        !subCategory || subCategory === 'undefined' ? null : subCategory,
      documents
    }))
    .orderBy('subCategory', 'desc')

    .value()
}

const CategorySection = ({
  categoryName,
  categoryDocs,
  index,
  archivingEnabled,
  canDelete,
  canEdit,
  onArchive,
  onDelete,
  onEdit,
  onRestore,
  carePlanNominative
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const list = groupBySubCategory(categoryDocs)
  const isCarePlanDocuments = !!categoryDocs[0]?.carePlanName
  const isArchivedSection =
    archivingEnabled && !!categoryDocs[0] && !!categoryDocs[0].archived
  const [isExpanded, setIsExpanded] = useState(!isArchivedSection)

  const containerStyle =
    index === 0 ? { ...styles.container, marginTop: 0 } : styles.container

  return (
    <div style={containerStyle}>
      {isArchivedSection ? (
        <button
          type="button"
          style={styles.archivedHeader}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          data-testid="documents-archived-header"
        >
          <SectionHeader title={t(reportsConstants.ARCHIVED_DOCUMENTS_TITLE)} />
          <Chevron direction={isExpanded ? 'up' : 'down'} />
        </button>
      ) : (
        <SectionHeader
          title={categoryName}
          subtitle={isCarePlanDocuments && getCarePlansNote(carePlanNominative)}
        />
      )}
      {(!isArchivedSection || isExpanded) && (
        <div style={styles.list}>
          {list.map(({ subCategory, documents }) => (
            <div key={subCategory || 'no-sub-category'}>
              {subCategory ? (
                <div style={styles.subCategory}>
                  <SectionHeader isSubSection greyLine title={subCategory} />
                </div>
              ) : null}
              {documents.map((document) => (
                <DocumentItem
                  archived={!!document.archived}
                  archivingEnabled={archivingEnabled}
                  canDelete={canDelete}
                  // A document removed from a care plan keeps
                  // attachedToCarePlan=true but loses its carePlanName, so it
                  // falls out of the read-only branch below. It must stay
                  // non-editable (View + Archive only, per AC1) — care plan
                  // documents are only edited via the care plan itself.
                  canEdit={canEdit && !document.attachedToCarePlan}
                  data={document}
                  key={document.id}
                  onArchive={onArchive}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onRestore={onRestore}
                  readOnly={
                    archivingEnabled
                      ? !document.archived && !!document.carePlanName
                      : document.attachedToCarePlan
                  }
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const DocumentList = ({
  archivingEnabled = false,
  canDelete = true,
  canEdit = true,
  documents,
  onArchive,
  onDelete,
  onEdit,
  onRestore,
  carePlanNominative,
  testId
}) => {
  return (
    <div data-testid={testId}>
      {Object.keys(documents).map((categoryName, index) => (
        <CategorySection
          key={categoryName}
          categoryName={categoryName}
          categoryDocs={documents[categoryName]}
          index={index}
          archivingEnabled={archivingEnabled}
          canDelete={canDelete}
          canEdit={canEdit}
          onArchive={onArchive}
          onDelete={onDelete}
          onEdit={onEdit}
          onRestore={onRestore}
          carePlanNominative={carePlanNominative}
        />
      ))}
    </div>
  )
}

const getCarePlansNote = (carePlanNominative) =>
  `To edit these documents you must review the ${getNominative(
    carePlanNominative,
    'Care Plans'
  )} they are attached to.`

const styles = {
  container: {
    margin: '40px 0px'
  },
  list: {
    listStyle: 'none',
    listStyleType: 'none',
    padding: 0
  },
  subCategory: {
    marginTop: 28
  },
  archivedHeader: {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    padding: 0,
    width: '100%'
  }
}

DocumentList.propTypes = {
  archivingEnabled: PropTypes.bool,
  canDelete: PropTypes.bool,
  canEdit: PropTypes.bool,
  documents: PropTypes.object,
  listId: PropTypes.string.isRequired,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRestore: PropTypes.func,
  testId: PropTypes.string
}

export default DocumentList
